import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';

import { MFA_TOTP_CONTRACT } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSInputLabel } from '@repo/sds/SDSInputLabel';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';
import { SDSText } from '@repo/sds/SDSText';
import { SDSTextInput } from '@repo/sds/SDSTextInput';
import { CountrySelect, countryCallingCode } from '@repo/ui/fixtures/country-select';

import smsSetupIntro from './assets/sms-setup-intro.svg';
import { MfaHero } from './mfa-hero';
import { MfaInvalidCodeDialog } from './mfa-invalid-code-dialog';
import { MfaOtpInput } from './mfa-otp-input';
import { MfaSettingsPage } from './mfa-settings-page';
import { formatResendClock, SMS_DEFAULT_COUNTRY, useMfaSmsSetupFlow } from './mfa-sms-setup-utils';

const MFA = DataTestIds.COMMON.SECURITY.MFA;
const TOTP_CODE_DIGITS = MFA_TOTP_CONTRACT.digits;

function MfaPhoneField({
  onDialCodeChange,
  onPhoneChange,
  phone,
  t,
}: {
  onDialCodeChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  phone: string;
  t: TFunction<'common'>;
}) {
  return (
    <SDSTextInput
      dataTestId={MFA.SMS_PHONE}
      isFullWidth
      isRequired
      label={t('security.mfa.smsPhoneLabel')}
      leftIcon={
        <CountrySelect
          compact
          dataTestId={MFA.SMS_DIAL}
          defaultCountry={SMS_DEFAULT_COUNTRY}
          display="callingCode"
          modalClassName="CountrySelect__modal__left-0"
          onSelectCountry={country => onDialCodeChange(countryCallingCode(country.code))}
          placement="top"
          showTriggerFlag={false}
        />
      }
      onChange={value => onPhoneChange(value.replace(/[^\d\s]/g, ''))}
      placeholder={t('security.mfa.smsPhonePlaceholder')}
      value={phone}
      wrapperClassName="MfaPhoneInput"
    />
  );
}

function MfaSmsStepCopy({ isVerify, masked, t }: { isVerify: boolean; masked: string; t: TFunction<'common'> }) {
  return (
    <SDSFlexbox direction="column" fullWidth gap="text-small">
      <SDSText
        color="primary"
        text={t('security.mfa.smsStep', { current: isVerify ? 2 : 1, total: 2 })}
        type="body-small"
      />
      <SDSText
        text={isVerify ? t('security.mfa.smsVerifyLead', { masked }) : t('security.mfa.smsSetupLead')}
        type="body-medium"
      />
    </SDSFlexbox>
  );
}

function MfaSmsResendMeta({
  busy,
  cooldownSeconds,
  onResendSms,
  t,
}: {
  busy: boolean;
  cooldownSeconds: number;
  onResendSms: () => Promise<boolean>;
  t: TFunction<'common'>;
}) {
  if (cooldownSeconds > 0) {
    return (
      <SDSText
        color="muted"
        text={t('security.mfa.smsResendIn', { time: formatResendClock(cooldownSeconds) })}
        type="body-small"
      />
    );
  }
  if (busy) {
    return null;
  }
  return (
    <SDSPrimitiveButton dataTestId={MFA.SMS_RESEND} onClick={() => void onResendSms()}>
      <SDSText color="primary" text={t('security.mfa.resend')} type="body-small" />
    </SDSPrimitiveButton>
  );
}

function MfaSmsVerifyFields({
  busy,
  code,
  cooldownSeconds,
  onChangeNumber,
  onResendSms,
  onSetupCodeChange,
  t,
}: {
  busy: boolean;
  code: string;
  cooldownSeconds: number;
  onChangeNumber: () => void;
  onResendSms: () => Promise<boolean>;
  onSetupCodeChange: (value: string) => void;
  t: TFunction<'common'>;
}) {
  return (
    <SDSFlexbox direction="column" fullWidth gap="text-medium">
      <SDSFlexbox direction="column" fullWidth gap="text-small">
        <SDSInputLabel isRequired label={t('security.mfa.smsCodeLabel')} />
        <MfaOtpInput onChange={onSetupCodeChange} value={code} />
      </SDSFlexbox>
      <div className="MfaSmsDialog__footerMeta">
        <MfaSmsResendMeta busy={busy} cooldownSeconds={cooldownSeconds} onResendSms={onResendSms} t={t} />
        <SDSPrimitiveButton dataTestId={MFA.SMS_CHANGE_NUMBER} onClick={onChangeNumber}>
          <SDSText color="primary" text={t('security.mfa.smsUseDifferentNumber')} type="body-small" />
        </SDSPrimitiveButton>
      </div>
    </SDSFlexbox>
  );
}

function MfaSmsInvalidDialog({
  isMobile,
  onCancel,
  onRetry,
  t,
}: {
  isMobile: boolean;
  onCancel: () => void;
  onRetry: () => void;
  t: TFunction<'common'>;
}) {
  return (
    <MfaInvalidCodeDialog
      backLabel={t('security.mfa.backToSecurity')}
      dataTestId={MFA.SMS_INVALID}
      description={t('security.mfa.smsInvalidDescription')}
      isMobile={isMobile}
      onClose={onCancel}
      primaryAction={{
        dataTestId: MFA.SMS_TRY_AGAIN,
        label: t('security.mfa.totpTryAgain'),
        onClick: onRetry,
      }}
      secondaryAction={{ dataTestId: MFA.CANCEL, label: t('security.mfa.cancel'), onClick: onCancel }}
      title={t('security.mfa.smsInvalidTitle')}
    />
  );
}

function MfaSmsMobileView({
  actionError,
  busy,
  code,
  goBackFromPhone,
  goToPhone,
  invalidMessage,
  isVerify,
  onCancel,
  onConfirmSetup,
  phoneField,
  phoneReady,
  sendCode,
  stepCopy,
  t,
  verifyFields,
}: {
  actionError?: string;
  busy: boolean;
  code: string;
  goBackFromPhone: () => void;
  goToPhone: () => void;
  invalidMessage: string;
  isVerify: boolean;
  onCancel: () => void;
  onConfirmSetup: () => void;
  phoneField: ReactNode;
  phoneReady: boolean;
  sendCode: () => void;
  stepCopy: ReactNode;
  t: TFunction<'common'>;
  verifyFields: ReactNode;
}) {
  return (
    <MfaSettingsPage
      actions={
        <>
          <SDSButton
            dataTestId={MFA.TOTP_BACK}
            isDisabled={busy}
            mode="outline"
            onClick={isVerify ? goToPhone : goBackFromPhone}
            text={t('security.mfa.totpBack')}
          />
          <SDSButton
            dataTestId={isVerify ? MFA.CONFIRM : MFA.SMS_SEND}
            isDisabled={isVerify ? (busy ? false : code.length !== TOTP_CODE_DIGITS) : !phoneReady}
            isSubmitting={busy}
            onClick={isVerify ? onConfirmSetup : sendCode}
            text={isVerify ? t('security.mfa.totpVerify') : t('security.mfa.smsSendCode')}
          />
        </>
      }
      ariaLabel={isVerify ? t('security.mfa.smsVerifyTitle') : t('security.mfa.smsSetupTitle')}
      backLabel={t('security.mfa.backToSecurity')}
      className={isVerify ? undefined : 'MfaSettingsPage--countryOverlay'}
      dataTestId={MFA.SMS_SETUP}
      onBack={onCancel}
    >
      <SDSFlexbox alignItems="center" direction="column" fullWidth gap="text-medium">
        <SDSText text={isVerify ? t('security.mfa.smsVerifyTitle') : t('security.mfa.smsSetupTitle')} type="h4" />
        <MfaHero plain src={smsSetupIntro} />
      </SDSFlexbox>
      {stepCopy}
      {isVerify ? verifyFields : phoneField}
      {actionError && actionError !== invalidMessage ? (
        <SDSText color="alert" dataTestId={MFA.ERROR} text={actionError} type="body-small" />
      ) : null}
    </MfaSettingsPage>
  );
}

function MfaSmsPhoneDialog({
  actionError,
  busy,
  goBackFromPhone,
  invalidMessage,
  onCancel,
  phoneField,
  phoneReady,
  sendCode,
  stepCopy,
  t,
}: {
  actionError?: string;
  busy: boolean;
  goBackFromPhone: () => void;
  invalidMessage: string;
  onCancel: () => void;
  phoneField: ReactNode;
  phoneReady: boolean;
  sendCode: () => void;
  stepCopy: ReactNode;
  t: TFunction<'common'>;
}) {
  return (
    <SDSDialog
      className="MfaTotpDialog MfaSmsSetupDialog"
      dataTestId={MFA.SMS_SETUP}
      isOpen
      onClose={onCancel}
      primaryAction={{
        dataTestId: MFA.SMS_SEND,
        disabled: busy || !phoneReady,
        label: t('security.mfa.smsSendCode'),
        loading: busy,
        onClick: sendCode,
      }}
      secondaryAction={{
        dataTestId: MFA.TOTP_BACK,
        label: t('security.mfa.totpBack'),
        onClick: goBackFromPhone,
      }}
      showCloseButton={false}
      size="small"
    >
      <SDSFlexbox direction="column" gap="text-x-large">
        <MfaHero src={smsSetupIntro} />
        <SDSFlexbox direction="column" gap="text-medium">
          <SDSText text={t('security.mfa.smsSetupTitle')} type="h4" />
          {stepCopy}
          {phoneField}
          {actionError && actionError !== invalidMessage ? (
            <SDSText color="alert" dataTestId={MFA.ERROR} text={actionError} type="body-small" />
          ) : null}
        </SDSFlexbox>
      </SDSFlexbox>
    </SDSDialog>
  );
}

function MfaSmsVerifyDialog({
  actionError,
  busy,
  code,
  goToPhone,
  invalidMessage,
  onCancel,
  onConfirmSetup,
  stepCopy,
  t,
  verifyFields,
}: {
  actionError?: string;
  busy: boolean;
  code: string;
  goToPhone: () => void;
  invalidMessage: string;
  onCancel: () => void;
  onConfirmSetup: () => void;
  stepCopy: ReactNode;
  t: TFunction<'common'>;
  verifyFields: ReactNode;
}) {
  return (
    <SDSDialog
      className="MfaTotpDialog MfaSmsSetupDialog"
      dataTestId={MFA.SMS_SETUP}
      isOpen
      onClose={onCancel}
      primaryAction={{
        dataTestId: MFA.CONFIRM,
        disabled: busy || code.length !== TOTP_CODE_DIGITS,
        label: t('security.mfa.totpVerify'),
        loading: busy,
        onClick: onConfirmSetup,
      }}
      secondaryAction={{
        dataTestId: MFA.TOTP_BACK,
        label: t('security.mfa.totpBack'),
        onClick: goToPhone,
      }}
      showCloseButton={false}
      size="small"
    >
      <SDSFlexbox direction="column" gap="text-x-large">
        <MfaHero src={smsSetupIntro} />
        <SDSFlexbox direction="column" gap="text-medium">
          <SDSText text={t('security.mfa.smsVerifyTitle')} type="h4" />
          {stepCopy}
          {verifyFields}
          {actionError && actionError !== invalidMessage ? (
            <SDSText color="alert" dataTestId={MFA.ERROR} text={actionError} type="body-small" />
          ) : null}
        </SDSFlexbox>
      </SDSFlexbox>
    </SDSDialog>
  );
}

export function MfaSmsSetupDialogs({
  actionError,
  busy,
  changePhone,
  code,
  cooldownSeconds,
  isMobile,
  onBackToMethods,
  onCancel,
  onClearActionError,
  onConfirmSetup,
  onManageSms,
  onResendSms,
  onSendSmsCode,
  onSetupCodeChange,
  phoneNumberSent,
  t,
}: {
  actionError?: string;
  busy: boolean;
  changePhone: boolean;
  code: string;
  cooldownSeconds: number;
  isMobile: boolean;
  onBackToMethods: () => void;
  onCancel: () => void;
  onClearActionError: () => void;
  onConfirmSetup: () => void;
  onManageSms: () => void;
  onResendSms: () => Promise<boolean>;
  onSendSmsCode: (phoneE164: string) => Promise<boolean>;
  onSetupCodeChange: (value: string) => void;
  phoneNumberSent?: string;
  t: TFunction<'common'>;
}) {
  const invalidMessage = t('security.mfa.invalidCode');
  const { effectivePhase, goToPhone, masked, phone, phoneReady, retryVerify, sendCode, setDialCode, setPhone } =
    useMfaSmsSetupFlow({
      actionError,
      busy,
      invalidMessage,
      onClearActionError,
      onSendSmsCode,
      onSetupCodeChange,
      phoneNumberSent,
    });

  const goBackFromPhone = changePhone ? onManageSms : onBackToMethods;
  const isVerify = effectivePhase === 'verify';

  const stepCopy = <MfaSmsStepCopy isVerify={isVerify} masked={masked} t={t} />;
  const phoneField = <MfaPhoneField onDialCodeChange={setDialCode} onPhoneChange={setPhone} phone={phone} t={t} />;
  const verifyFields = (
    <MfaSmsVerifyFields
      busy={busy}
      code={code}
      cooldownSeconds={cooldownSeconds}
      onChangeNumber={goToPhone}
      onResendSms={onResendSms}
      onSetupCodeChange={onSetupCodeChange}
      t={t}
    />
  );

  if (effectivePhase === 'invalid') {
    return <MfaSmsInvalidDialog isMobile={isMobile} onCancel={onCancel} onRetry={retryVerify} t={t} />;
  }

  if (isMobile) {
    return (
      <MfaSmsMobileView
        actionError={actionError}
        busy={busy}
        code={code}
        goBackFromPhone={goBackFromPhone}
        goToPhone={goToPhone}
        invalidMessage={invalidMessage}
        isVerify={isVerify}
        onCancel={onCancel}
        onConfirmSetup={onConfirmSetup}
        phoneField={phoneField}
        phoneReady={phoneReady}
        sendCode={sendCode}
        stepCopy={stepCopy}
        t={t}
        verifyFields={verifyFields}
      />
    );
  }

  if (effectivePhase === 'phone') {
    return (
      <MfaSmsPhoneDialog
        actionError={actionError}
        busy={busy}
        goBackFromPhone={goBackFromPhone}
        invalidMessage={invalidMessage}
        onCancel={onCancel}
        phoneField={phoneField}
        phoneReady={phoneReady}
        sendCode={sendCode}
        stepCopy={stepCopy}
        t={t}
      />
    );
  }

  return (
    <MfaSmsVerifyDialog
      actionError={actionError}
      busy={busy}
      code={code}
      goToPhone={goToPhone}
      invalidMessage={invalidMessage}
      onCancel={onCancel}
      onConfirmSetup={onConfirmSetup}
      stepCopy={stepCopy}
      t={t}
      verifyFields={verifyFields}
    />
  );
}
